import { Alert, Box, Button, LinearProgress, Link, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { MyListApi } from "../utils/restApi";
import { notification } from "../utils/utils";
import { ItemComments } from "./ItemComments";
import { useDispatch, useSelector } from "react-redux";
import { ItemSocialStats } from "./ItemStats";
import { ItemProperty, PropertyEditButton } from "./ItemProperty";
import { setItem, clear } from '../slices/itemContextSlice';
import { ItemCard, ItemIdentity, StatusBadge } from "./ui/ItemCard";
import { SharedExperiences } from "./ui/ActionCounts";
import { APP_SCROLL_CONTAINER_ID } from "./Layout";
import {
  formatLabel,
  getActionStats,
  getDescriptionProperty,
  getItemData,
  getItemDescription,
  getItemImageUrl,
  getTagDataInfo,
  getUserActionNames,
} from "../utils/itemUtil";

const DOCK_SPACE = '96px';

const ExperienceAction = ({ item, user, buttonRef, fullWidth = false }) => {
  const navigate = useNavigate();
  const handleClick = () => {
    navigate(user ? `/share-experience/${item.id}` : '/login');
  };
  return (
    <Button
      ref={buttonRef}
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleClick}
      fullWidth={fullWidth}
      sx={{ minHeight: 44, px: 2.5, flexShrink: 0 }}
    >
      {user ? 'Share your experience' : 'Log in to share'}
    </Button>
  );
};

// Reports whether an element is inside the app's scroll container viewport.
const useInView = (ref, enabled) => {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { root: document.getElementById(APP_SCROLL_CONTAINER_ID) },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);
  return inView;
};

export const ItemDetails = () => {

  const dispatch = useDispatch();

  const user = useSelector(state => state.login.user);
  const item = useSelector(state => state.itemContext.item);

  const { itemId } = useParams();
  const [ error, setError ] = useState(null);
  const [ editing, setEditing ] = useState(false);
  const primaryActionRef = useRef(null);
  const isLoaded = Boolean(item && String(item.id) === String(itemId));
  const primaryInView = useInView(primaryActionRef, isLoaded);
  const showDock = isLoaded && !primaryInView && !editing;

  useEffect(() => {
    let cancelled = false;
    if (!item || String(item.id) !== String(itemId)) {
      setError(null);
      MyListApi.getItem(itemId)
        .then(data => {
          if (!cancelled) dispatch(setItem(data));
        })
        .catch(e => {
          if (!cancelled) setError(e?.message || 'This item could not be loaded.');
        });
    }
    return () => {
      cancelled = true;
      if (item) {
        dispatch(clear());
      }
    }
  }, [item, itemId, dispatch]);

  // Keep focused fields clear of the sticky sharing bar.
  useEffect(() => {
    const container = document.getElementById(APP_SCROLL_CONTAINER_ID);
    if (!container) return undefined;
    container.style.scrollPaddingBottom = showDock ? DOCK_SPACE : '';
    return () => { container.style.scrollPaddingBottom = ''; };
  }, [showDock]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!isLoaded) {
    return <LinearProgress aria-label="Loading item" />;
  }

  const data = getItemData(item);
  const dataInfo = getTagDataInfo(item.tag);
  const descriptionProperty = getDescriptionProperty(item);
  const description = getItemDescription(item);
  const detailProperties = dataInfo.filter(di => di.type !== 'item_link' && di.name !== descriptionProperty?.name);
  const myActions = getUserActionNames(item);
  const groupedItemLinks = (item.itemLinks || []).reduce((acc, link) => {
    if (!acc[link.name]) {
      acc[link.name] = [];
    }
    acc[link.name].push(link);
    return acc;
  }, {});
  const hasLinks = Object.keys(groupedItemLinks).length > 0;

  const handleApprove = () => {
    MyListApi.approveItem(item.id)
      .then(_ => {
        notification("Item approved", "success");
      })
      .catch(error => {
        notification(error.message, "error");
      });
  }

  const handleReject = () => {
    MyListApi.rejectItem(item.id)
      .then(_ => {
        notification("Item rejected", "success");
      })
      .catch(error => {
        notification(error.message, "error");
      });
  }

  const isTextEntry = target => ['INPUT', 'TEXTAREA'].includes(target?.tagName) || target?.isContentEditable;
  const handleFocus = e => { if (isTextEntry(e.target)) setEditing(true); };
  const handleBlur = e => { if (isTextEntry(e.target)) setEditing(false); };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}
      onFocusCapture={handleFocus}
      onBlurCapture={handleBlur}
    >
      {(user?.isAdmin && item?.isPending) ?
        <Paper variant="outlined" sx={{ p: '16px 20px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <Typography sx={{ fontWeight: 600, mr: 'auto' }}>Admin · this item is pending approval</Typography>
          <Button variant="outlined" onClick={handleReject}>Reject</Button>
          <Button variant="contained" onClick={handleApprove}>Approve</Button>
        </Paper>
      : null}

      {/* 1. Item identity and summary */}
      <ItemCard
        component="section"
        imageUrl={getItemImageUrl(item)}
        mediaResetKey={item.id}
        imageAlt={`Image for ${item.name}`}
        sx={{ '--photo-width': '28%' }}
        contentSx={{ p: { xs: '18px', sm: '24px' } }}
      >
        <ItemIdentity item={item} titleComponent="h1" titleVariant="h1" link={false}>
          {item.isPending ? <StatusBadge>Pending approval</StatusBadge> : null}
        </ItemIdentity>
        {descriptionProperty ?
          <Box sx={{ mt: '15px', display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
            <Typography sx={{ overflowWrap: 'anywhere', color: description ? 'text.primary' : 'text.secondary' }}>
              {description || 'No description yet.'}
            </Typography>
            <PropertyEditButton itemId={item.id} propertyName={descriptionProperty.name} itemUpdates={item.itemUpdates} />
          </Box>
        : null}
      </ItemCard>

      {/* 2. The user's own experience and the primary sharing action */}
      <Paper
        component="section"
        variant="outlined"
        aria-labelledby="your-experience-heading"
        sx={{
          borderRadius: '12px',
          borderLeft: '3px solid',
          borderLeftColor: 'sage.main',
          p: { xs: '17px', sm: '20px' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '18px',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography id="your-experience-heading" component="h2" variant="h3">Your experience</Typography>
          {!user ?
            <Typography variant="body2" color="text.secondary">Log in to share what you thought of it.</Typography>
            : myActions.length ?
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', mt: '6px' }}>
                <Typography variant="body2" color="text.secondary">You shared</Typography>
                {myActions.map(a => <StatusBadge key={a}>{formatLabel(a)}</StatusBadge>)}
              </Box>
              : <Typography variant="body2" color="text.secondary">You haven't shared an experience yet.</Typography>
          }
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <ExperienceAction item={item} user={user} buttonRef={primaryActionRef} fullWidth />
        </Box>
      </Paper>

      {/* 3. Shared experiences */}
      <SharedExperiences stats={getActionStats(item)} userActions={myActions} />

      {/* 4. Properties, related items, secondary stats, comments */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: hasLinks ? '1.35fr 1fr' : 'minmax(0, 1fr)' }, gap: '28px' }}>
        <Box component="section" aria-labelledby="more-details-heading" sx={{ minWidth: 0 }}>
          <Typography id="more-details-heading" component="h2" variant="h3">More details</Typography>
          {detailProperties.length ?
            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: '12px 16px', m: 0, mt: '12px' }}>
              {detailProperties.map(di => (
                <ItemProperty key={di.name} itemId={item.id} dataInfo={di} data={data} itemUpdates={item.itemUpdates} />
              ))}
            </Box>
            : <Typography variant="body2" color="text.secondary" sx={{ mt: '8px' }}>No additional details.</Typography>
          }
        </Box>
        {hasLinks ?
          <Box component="section" aria-labelledby="related-heading" sx={{ minWidth: 0 }}>
            <Typography id="related-heading" component="h2" variant="h3">Related</Typography>
            {Object.entries(groupedItemLinks).map(([name, links]) => (
              <Box key={name} sx={{ mt: '12px' }}>
                <Typography component="h3" variant="caption" color="text.secondary">{formatLabel(name)}</Typography>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {links.map((link, ix) => (
                    <Box component="li" key={`${link.itemId}-${ix}`} sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '10px', borderBottom: 1, borderColor: 'divider' }}>
                      <LinkIcon aria-hidden="true" sx={{ color: 'sage.main', fontSize: 18 }} />
                      <Link component={RouterLink} to={`/items/${link.itemId}`} color="text.primary" sx={{ overflowWrap: 'anywhere' }}>{link.itemName}</Link>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        : null}
      </Box>

      <ItemSocialStats item={item} showComments={false} />

      <Box component="section" aria-labelledby="comments-heading">
        <ItemComments itemId={item.id} count={item.commentCount} headingId="comments-heading" />
      </Box>

      {showDock ?
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 3,
            mx: { xs: -2, sm: -3 },
            mb: -3,
            px: { xs: 2, sm: 3 },
            pt: '12px',
            pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
          }}
        >
          <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</Typography>
            <Typography variant="caption" color="text.secondary">Your next story starts here.</Typography>
          </Box>
          <Box sx={{ flex: { xs: 1, sm: '0 0 auto' } }}>
            <ExperienceAction item={item} user={user} fullWidth />
          </Box>
        </Box>
      : null}
    </Box>
  )
}
